import React, { useCallback, useMemo, useState } from 'react';
import { uuidv4 } from '../../util/uuidv4';
import UserNotifications from './userNotifications';

export enum AlpNotificationType {
  SUCCESS,
  ERROR,
  REMOVE_ALL,
  WARNING,
}

export type Message = string | string[];

export type AlpNotification = {
  id: string;
  type: AlpNotificationType;
  message: string;
};

type OneOrManyAlpNotification = AlpNotification | AlpNotification[];

interface NotificationFunctionStore {
  /**
   * @deprecated AddNotification should be avoided for direct usage.
   * Use more specific sub-methods instead.
   * */
  addNotification: (notification: OneOrManyAlpNotification) => void;
  removeNotification: (notification: AlpNotification) => void;
  addSuccessNotification: (message: Message) => void;
  addErrorNotification: (message: Message) => void;
  addWarningNotification: (message: Message) => void;
}

export const NotificationFunctionContext =
  React.createContext<NotificationFunctionStore>({
    removeNotification: () => {},
    addNotification: () => {},
    addSuccessNotification: () => {},
    addErrorNotification: () => {},
    addWarningNotification: () => {},
  });

const NotificationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<AlpNotification[]>([]);

  const addNotification = useCallback(
    (oneOrManyNotifications: OneOrManyAlpNotification) => {
      setNotifications((prevNotifications) => {
        const updatedNotifications = prevNotifications.concat(
          oneOrManyNotifications,
        );

        const shouldAddRemoveAll =
          updatedNotifications.length > 0 &&
          !updatedNotifications.some(
            (notification) =>
              notification.type === AlpNotificationType.REMOVE_ALL,
          );

        return shouldAddRemoveAll
          ? [
              {
                id: uuidv4(),
                type: AlpNotificationType.REMOVE_ALL,
                message: '',
              },
            ].concat(updatedNotifications)
          : updatedNotifications;
      });
    },
    [],
  );

  const addNotificationByType = useCallback(
    (message: Message, type: AlpNotificationType) => {
      if (typeof message === 'string') {
        addNotification({ id: uuidv4(), type, message: message as string });
      } else {
        addNotification(
          (message as string[]).map((msg) => ({
            id: uuidv4(),
            type,
            message: msg,
          })),
        );
      }
    },
    [addNotification],
  );

  const removeNotification = useCallback(
    (notification: AlpNotification) => {
      if (notification.type === AlpNotificationType.REMOVE_ALL) {
        setNotifications([]);
      } else {
        const toKeep = notifications.filter((n) => n !== notification);
        const onlyRemoveAllLeft =
          toKeep.length === 1 &&
          toKeep[0].type === AlpNotificationType.REMOVE_ALL;

        setNotifications(onlyRemoveAllLeft ? [] : toKeep);
      }
    },
    [notifications],
  );

  const notificationFunctionContextValue = useMemo(() => {
    const addSuccessNotification = (message: Message) =>
      addNotificationByType(message, AlpNotificationType.SUCCESS);

    const addErrorNotification = (message: Message) =>
      addNotificationByType(message, AlpNotificationType.ERROR);

    const addWarningNotification = (message: Message) =>
      addNotificationByType(message, AlpNotificationType.WARNING);

    return {
      addNotification,
      addSuccessNotification,
      addErrorNotification,
      addWarningNotification,
      removeNotification,
    };
  }, [addNotification, removeNotification, addNotificationByType]);

  return (
    <NotificationFunctionContext.Provider
      value={notificationFunctionContextValue}
    >
      <UserNotifications notifications={notifications} />
      {children}
    </NotificationFunctionContext.Provider>
  );
};

export default NotificationProvider;
